import { useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { LoginPage } from "../components/index.js";
import { useApiClient } from "../api/ApiContext.jsx";
import { useAuth } from "../auth/AuthContext.jsx";
import AuthLayout from "../layout/AuthLayout.jsx";
import { InlineMessage, errorText } from "../ui/Feedback.jsx";

const PROVIDER_LABEL = { google: "Google", github: "GitHub" };

/**
 * The reasons `oauthFailed` can put in the landing URL's `?error=` query.
 *
 * The server redirects rather than answering JSON, so the code is the only thing
 * that survives the round trip; without a message here the customer sees the
 * login screen and a bare `401 not signed in` from the session bootstrap, with
 * no way to tell a cancelled consent from a wrong client secret. Unmapped codes
 * still surface, so a new reason is visible rather than silent.
 */
const OAUTH_ERROR_MESSAGES = {
  email_in_use:
    "An account already exists for that email address. Sign in with your password — GitHub/Google sign-in does not attach to an existing account.",
  denied: "The provider refused the sign-in — you may have cancelled it. Try again, or sign in with email and password.",
  bad_state: "That sign-in link expired or was already used. Start the sign-in again.",
  no_code: "The provider did not return an authorization code. Start the sign-in again.",
  exchange_failed:
    "We could not complete the sign-in with the provider. Try again; if it keeps failing, sign in with email and password.",
  not_configured: "Federated sign-in is not configured on this server. Use email and password instead.",
};

function oauthErrorMessage(code) {
  if (!code) return null;
  return OAUTH_ERROR_MESSAGES[code] ?? `Sign-in failed (${code}). Try again, or use email and password.`;
}

export default function LoginScreen() {
  const api = useApiClient();
  const { adoptSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const nextPath = location.state?.from?.pathname || "/";
  const oauthFailure = oauthErrorMessage(searchParams.get("error"));

  async function handleSubmit({ email, password }) {
    if (busy) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const data = await api.auth.login({ email, password });
      if (data?.challenge_required) {
        navigate("/2fa", {
          state: {
            challengeId: data.challenge_id,
            destination: data.destination,
            purpose: data.purpose ?? "login_2fa",
            nextPath,
          },
        });
        return;
      }
      adoptSession(data);
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(errorText(err, "Could not log in."));
    } finally {
      setBusy(false);
    }
  }

  async function handleSocialLogin(provider) {
    setError(null);
    setNotice(null);
    try {
      const result = await api.auth.startOAuth(provider);
      if (result?.redirectUrl) {
        window.location.assign(result.redirectUrl);
        return;
      }
      if (result?.data?.challenge_required) {
        navigate("/2fa", {
          state: {
            challengeId: result.data.challenge_id,
            destination: result.data.destination,
            nextPath,
          },
        });
        return;
      }
      if (result?.data?.user) {
        adoptSession(result.data);
        navigate(nextPath, { replace: true });
      }
    } catch (err) {
      if (err?.isNotConfigured) {
        // 503 not_configured: say so inline instead of failing silently.
        setNotice(
          err.message ||
            `${PROVIDER_LABEL[provider] ?? provider} sign-in is not configured on this server yet. Use email and password instead.`,
        );
      } else {
        setError(errorText(err, "Social login failed."));
      }
    }
  }

  return (
    <AuthLayout>
      {oauthFailure ? <InlineMessage tone="danger">{oauthFailure}</InlineMessage> : null}
      {notice ? <InlineMessage tone="warning">{notice}</InlineMessage> : null}
      {error ? <InlineMessage tone="danger">{error}</InlineMessage> : null}
      <LoginPage
        onSubmit={handleSubmit}
        onSocialLogin={handleSocialLogin}
        onForgotPassword={() => navigate("/reset-password")}
        onGoToSignup={() => navigate("/signup")}
      />
    </AuthLayout>
  );
}
